import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EMPLOYEE_DOCUMENT_ACCEPT,
  EMPLOYEE_DOCUMENT_CATEGORIES,
  deleteEmployeeDocument,
  downloadEmployeeDocument,
  getDocumentCategoryLabel,
  getDocumentFieldLabels,
  getEmployeeDocumentBlobUrl,
  listEmployeeDocuments,
  updateEmployeeDocument,
  uploadEmployeeDocument,
  validateEmployeeDocumentFile,
} from "../../api/employeeDocuments";
import { isNative } from "../../utils/mobile";
import "./EmployeeDocumentsPanel.css";

function isImage(mimeType = "", name = "") {
  return mimeType.startsWith("image/") || /\.(jpe?g|png)$/i.test(name);
}

function isPdf(mimeType = "", name = "") {
  return mimeType === "application/pdf" || /\.pdf$/i.test(name);
}

function formatBytes(size = 0) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("ar-EG");
  } catch {
    return value;
  }
}

function toDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function expiryLabel(status) {
  switch (status) {
    case "expired":
      return "منتهي";
    case "expiring_soon":
      return "ينتهي قريباً";
    case "valid":
      return "ساري";
    default:
      return "بدون صلاحية";
  }
}

const EMPTY_META = {
  documentNumber: "",
  issuedAt: "",
  expiresAt: "",
  notes: "",
};

export default function EmployeeDocumentsPanel({
  employeeId,
  selfMode = false,
  onChanged,
}) {
  const [documents, setDocuments] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    category: "",
    ...EMPTY_META,
  });

  const [form, setForm] = useState({
    category: "NATIONAL_ID",
    ...EMPTY_META,
    file: null,
  });

  const uploadCategories = useMemo(
    () =>
      selfMode
        ? EMPLOYEE_DOCUMENT_CATEGORIES.filter((cat) => cat.value !== "CONTRACT")
        : EMPLOYEE_DOCUMENT_CATEGORIES,
    [selfMode],
  );
  const uploadLabels = useMemo(
    () => getDocumentFieldLabels(form.category),
    [form.category],
  );
  const editLabels = useMemo(
    () => getDocumentFieldLabels(editForm.category),
    [editForm.category],
  );

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listEmployeeDocuments(employeeId);
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setCompletion(data?.completion || null);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر جلب مستندات الموظف",
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const docsByCategory = useMemo(() => {
    const map = {};
    for (const cat of EMPLOYEE_DOCUMENT_CATEGORIES) {
      map[cat.value] = [];
    }
    for (const doc of documents) {
      if (!map[doc.category]) map[doc.category] = [];
      map[doc.category].push(doc);
    }
    return map;
  }, [documents]);

  async function handleUpload(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    const validationError = validateEmployeeDocumentFile(form.file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await uploadEmployeeDocument(
        employeeId,
        {
          file: form.file,
          category: form.category,
          documentNumber: form.documentNumber || undefined,
          issuedAt: form.issuedAt || undefined,
          expiresAt: form.expiresAt || undefined,
          notes: form.notes || undefined,
        },
        setProgress,
      );
      setSuccess("تم رفع المستند بنجاح");
      setForm((prev) => ({
        ...prev,
        file: null,
        ...EMPTY_META,
      }));
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message) ? message.join("\n") : message || "تعذر رفع الملف",
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function handleView(doc) {
    setBusyId(doc.id);
    setError("");
    try {
      // Android WebView often blanks PDF iframes — open via native share instead.
      if (isNative() && (isPdf(doc.mimeType, doc.originalName) || !isImage(doc.mimeType, doc.originalName))) {
        await downloadEmployeeDocument(doc.id, doc.originalName);
        return;
      }

      const url = await getEmployeeDocumentBlobUrl(doc.id);
      if (preview?.url) URL.revokeObjectURL(preview.url);
      setPreview({
        url,
        mimeType: doc.mimeType,
        name: doc.originalName,
        isPdf: isPdf(doc.mimeType, doc.originalName),
      });
    } catch (err) {
      setError(err.message || "تعذر عرض الملف");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDownload(doc) {
    setBusyId(doc.id);
    setError("");
    try {
      await downloadEmployeeDocument(doc.id, doc.originalName);
    } catch (err) {
      setError(err.message || "تعذر تحميل الملف");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`حذف المستند "${doc.originalName}"؟`)) return;
    setBusyId(doc.id);
    setError("");
    setSuccess("");
    try {
      await deleteEmployeeDocument(doc.id);
      setSuccess("تم حذف المستند");
      if (editId === doc.id) setEditId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message) ? message.join("\n") : message || "تعذر حذف الملف",
      );
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(doc) {
    setEditId(doc.id);
    setEditForm({
      category: doc.category,
      documentNumber: doc.documentNumber || "",
      issuedAt: toDateInput(doc.issuedAt),
      expiresAt: toDateInput(doc.expiresAt),
      notes: doc.notes || "",
    });
    setError("");
    setSuccess("");
  }

  async function saveEdit(event) {
    event.preventDefault();
    if (!editId) return;
    setBusyId(editId);
    setError("");
    setSuccess("");
    try {
      await updateEmployeeDocument(editId, {
        category: editForm.category,
        documentNumber: editForm.documentNumber || null,
        issuedAt: editForm.issuedAt || null,
        expiresAt: editForm.expiresAt || null,
        notes: editForm.notes || null,
      });
      setSuccess("تم تحديث المستند");
      setEditId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر تحديث المستند",
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="edp" dir="rtl">
      {completion && !selfMode && (
        <div className="edp-completion">
          <div className="edp-completion-head">
            <div>
              <h3 className="edp-title">اكتمال الملف</h3>
              <p className="edp-subtitle">
                {completion.present?.length || 0} من{" "}
                {completion.required?.length || 0} مستندات مطلوبة
              </p>
            </div>
            <div className="edp-percent">{completion.percent ?? 0}%</div>
          </div>
          <div className="edp-progress-track">
            <div
              className="edp-progress-fill"
              style={{ width: `${completion.percent ?? 0}%` }}
            />
          </div>
          {completion.missing?.length > 0 && (
            <div className="edp-missing">
              <strong>ناقص:</strong>{" "}
              {completion.missing
                .map((cat) => getDocumentCategoryLabel(cat))
                .join(" · ")}
            </div>
          )}
          {(completion.expired?.length > 0 ||
            completion.expiringSoon?.length > 0) && (
            <div className="edp-alerts">
              {completion.expired?.length > 0 && (
                <div className="edp-alert edp-alert-danger">
                  مستندات منتهية ({completion.expired.length}):{" "}
                  {completion.expired
                    .map((d) => d.originalName)
                    .join(" · ")}
                </div>
              )}
              {completion.expiringSoon?.length > 0 && (
                <div className="edp-alert edp-alert-warn">
                  تنتهي خلال 30 يوماً ({completion.expiringSoon.length}):{" "}
                  {completion.expiringSoon
                    .map((d) => d.originalName)
                    .join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <form className="edp-upload" onSubmit={handleUpload}>
        <h3 className="edp-title">رفع مستند</h3>
        <p className="edp-subtitle">
          ارفع صورة/ملف المستند وأدخل بياناته يدوياً (الرقم وتواريخ الإصدار
          والانتهاء)
          {selfMode
            ? " — عقد العمل يرفعه شؤون الموظفين ولا يظهر في قائمة الرفع."
            : ""}
        </p>
        <div className="edp-upload-grid">
          <label className="edp-field">
            <span>الفئة</span>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, category: e.target.value }))
              }
              disabled={uploading}
            >
              {uploadCategories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                  {cat.required ? "" : " (اختياري)"}
                </option>
              ))}
            </select>
          </label>
          <label className="edp-field">
            <span>{uploadLabels.documentNumber}</span>
            <input
              type="text"
              value={form.documentNumber}
              maxLength={100}
              placeholder={uploadLabels.documentNumberPlaceholder}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  documentNumber: e.target.value,
                }))
              }
              disabled={uploading}
            />
          </label>
          <label className="edp-field">
            <span>{uploadLabels.issuedAt}</span>
            <input
              type="date"
              value={form.issuedAt}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, issuedAt: e.target.value }))
              }
              disabled={uploading}
            />
          </label>
          <label className="edp-field">
            <span>{uploadLabels.expiresAt}</span>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, expiresAt: e.target.value }))
              }
              disabled={uploading}
            />
          </label>
          <label className="edp-field edp-field-wide">
            <span>ملاحظات</span>
            <input
              type="text"
              value={form.notes}
              maxLength={500}
              placeholder="اختياري"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              disabled={uploading}
            />
          </label>
          <label className="edp-field edp-field-wide">
            <span>صورة / ملف المستند</span>
            <input
              type="file"
              accept={EMPLOYEE_DOCUMENT_ACCEPT}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  file: e.target.files?.[0] || null,
                }))
              }
              disabled={uploading}
            />
          </label>
        </div>
        {form.file && (
          <p className="edp-file-meta">
            {form.file.name} · {formatBytes(form.file.size)}
          </p>
        )}
        {uploading && (
          <div className="edp-progress-track">
            <div className="edp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
        <button
          type="submit"
          className="edp-btn edp-btn-primary"
          disabled={uploading || !form.file}
        >
          {uploading ? `جاري الرفع... ${progress}%` : "رفع المستند"}
        </button>
      </form>

      {error && <p className="edp-error">{error}</p>}
      {success && <p className="edp-success">{success}</p>}
      {loading && <p className="edp-muted">جاري تحميل المستندات...</p>}

      <div className="edp-categories">
        {EMPLOYEE_DOCUMENT_CATEGORIES.map((cat) => {
          const items = docsByCategory[cat.value] || [];
          const isMissing =
            cat.required && completion?.missing?.includes(cat.value);
          const labels = getDocumentFieldLabels(cat.value);
          return (
            <section key={cat.value} className="edp-category">
              <div className="edp-category-head">
                <h4>
                  {cat.label}
                  {cat.required ? (
                    <span className="edp-badge required" title="مطلوب">
                      *
                    </span>
                  ) : (
                    <span className="edp-badge optional">اختياري</span>
                  )}
                  {isMissing && (
                    <span className="edp-badge missing">ناقص</span>
                  )}
                </h4>
                <span className="edp-count">{items.length}</span>
              </div>
              {items.length === 0 ? (
                <p className="edp-muted">لا توجد ملفات في هذه الفئة</p>
              ) : (
                <ul className="edp-list">
                  {items.map((doc) => (
                    <li key={doc.id} className="edp-item">
                      <div className="edp-item-main">
                        <div className="edp-item-name">{doc.originalName}</div>
                        <div className="edp-item-meta">
                          {formatBytes(doc.size)} · رفع{" "}
                          {formatDate(doc.createdAt)}
                          {doc.uploadedByName
                            ? ` · بواسطة ${doc.uploadedByName}`
                            : ""}
                        </div>
                        {doc.documentNumber && (
                          <div className="edp-item-meta">
                            {labels.documentNumber}:{" "}
                            <strong>{doc.documentNumber}</strong>
                          </div>
                        )}
                        <div className="edp-item-meta">
                          {labels.issuedAt}: {formatDate(doc.issuedAt)}
                          {" · "}
                          {labels.expiresAt}: {formatDate(doc.expiresAt)}{" "}
                          <span
                            className={`edp-expiry edp-expiry-${doc.expiryStatus}`}
                          >
                            {expiryLabel(doc.expiryStatus)}
                          </span>
                        </div>
                        {doc.notes && (
                          <div className="edp-item-notes">{doc.notes}</div>
                        )}
                      </div>
                      <div className="edp-item-actions">
                        {(isImage(doc.mimeType, doc.originalName) ||
                          isPdf(doc.mimeType, doc.originalName)) && (
                          <button
                            type="button"
                            className="edp-btn"
                            disabled={busyId === doc.id}
                            onClick={() => handleView(doc)}
                          >
                            عرض
                          </button>
                        )}
                        <button
                          type="button"
                          className="edp-btn"
                          disabled={busyId === doc.id}
                          onClick={() => handleDownload(doc)}
                        >
                          تحميل
                        </button>
                        {doc.canDelete !== false && (
                          <>
                            <button
                              type="button"
                              className="edp-btn"
                              disabled={busyId === doc.id}
                              onClick={() => startEdit(doc)}
                            >
                              تعديل
                            </button>
                            <button
                              type="button"
                              className="edp-btn edp-btn-danger"
                              disabled={busyId === doc.id}
                              onClick={() => handleDelete(doc)}
                            >
                              حذف
                            </button>
                          </>
                        )}
                      </div>
                      {editId === doc.id && (
                        <form className="edp-edit" onSubmit={saveEdit}>
                          <label className="edp-field">
                            <span>الفئة</span>
                            <select
                              value={editForm.category}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  category: e.target.value,
                                }))
                              }
                            >
                              {uploadCategories.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="edp-field">
                            <span>{editLabels.documentNumber}</span>
                            <input
                              type="text"
                              maxLength={100}
                              value={editForm.documentNumber}
                              placeholder={
                                editLabels.documentNumberPlaceholder
                              }
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  documentNumber: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="edp-field">
                            <span>{editLabels.issuedAt}</span>
                            <input
                              type="date"
                              value={editForm.issuedAt}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  issuedAt: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="edp-field">
                            <span>{editLabels.expiresAt}</span>
                            <input
                              type="date"
                              value={editForm.expiresAt}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  expiresAt: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <label className="edp-field edp-field-wide">
                            <span>ملاحظات</span>
                            <input
                              type="text"
                              maxLength={500}
                              value={editForm.notes}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  notes: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <div className="edp-edit-actions">
                            <button
                              type="submit"
                              className="edp-btn edp-btn-primary"
                              disabled={busyId === doc.id}
                            >
                              حفظ
                            </button>
                            <button
                              type="button"
                              className="edp-btn"
                              onClick={() => setEditId(null)}
                            >
                              إلغاء
                            </button>
                          </div>
                        </form>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {preview && (
        <div
          className="edp-preview-overlay"
          onClick={() => {
            if (preview?.url) URL.revokeObjectURL(preview.url);
            setPreview(null);
          }}
        >
          <div
            className="edp-preview-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="edp-preview-head">
              <strong>{preview.name}</strong>
              <button
                type="button"
                className="edp-btn"
                onClick={() => {
                  if (preview?.url) URL.revokeObjectURL(preview.url);
                  setPreview(null);
                }}
              >
                إغلاق
              </button>
            </div>
            {preview.isPdf ? (
              <iframe title={preview.name} src={preview.url} />
            ) : (
              <img src={preview.url} alt={preview.name} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
